import { parseScript } from "../transform-script-engine";
import type { TransformFunctionStatement, TransformFunctionParam } from "../transform-script-engine/types";
import {
  resolveTransformScriptBinding,
  resolveTransformScriptBindingsInTemplate,
  type TransformScriptBindingContext,
} from "./binding";
import {
  datePartsToUnixSeconds,
  formatDatePartsWithPattern,
  parseBestEffortDateParts,
  parseDatePartsWithPattern,
  shiftDatePartsByUtcOffset,
  unixSecondsToDateParts,
} from "./temporal";

export interface TransformScriptEvaluationInput {
  input: string;
  references: Record<string, string>;
}

export interface TransformScriptEvaluationResult {
  output: string;
  renamedField: string;
  unsupportedFunctions: string[];
}

const OFFLINE_SUPPORTED_FUNCTIONS = new Set([
  "trim_space",
  "lowercase",
  "uppercase",
  "collapse_space",
  "keep_digits",
  "remove_prefix",
  "remove_suffix",
  "default_if_empty",
  "replace_if_not_number",
  "map_value",
  "add",
  "subtract",
  "multiply",
  "divide",
  "round",
  "ceil",
  "floor",
  "format_decimal",
  "clamp",
  "sum_fields",
  "prefix_with",
  "subfix_with",
  "autonumber",
  "base64_to_s3",
  "encrypt_to_s3",
  "s3_to_decrypt",
  "s3_to_base64",
  "concat",
  "overwrite_value",
  "replace_string",
  "split_string",
  "explode_by_pipe",
  "repeat_string",
  "read_from_json",
  "read_from_array",
  "build_column",
  "exit_if_equal",
  "hash",
  "datetime",
  "to_system_datetime",
  "datetime_to_unix_timestamp",
  "unix_timestamp_to_datetime",
  "format_date_string",
  "from_system_datetime",
  "generate_jwt",
  "convert_timezone",
  "hex_utf16",
  "math_expressions",
  "add_region_phone_number",
  "convert_redirect_id_to_contact_id",
  "decode",
]);

function resolveParamValue(
  param: TransformFunctionParam,
  currentInput: string,
  references: Record<string, string>
) {
  const bindingContext: TransformScriptBindingContext = {
    selfValue: currentInput,
    hasSelf: true,
    refs: references,
  };

  if (param.type !== "reference") {
    return resolveTransformScriptBindingsInTemplate(param.value, bindingContext);
  }

  if (param.value === "_self") {
    return currentInput;
  }

  if (param.value in references) {
    return resolveTransformScriptBindingsInTemplate(references[param.value] ?? "", bindingContext);
  }

  throw new Error(`reference $${param.value} not found`);
}

function resolveDefaultValue(
  param: TransformFunctionParam | undefined,
  currentInput: string,
  references: Record<string, string>
) {
  if (!param) {
    throw new Error("transform function failed and no default value is set");
  }

  return resolveParamValue(param, currentInput, references);
}

async function shaHash(algorithm: string, value: string) {
  const normalized = algorithm.toUpperCase();

  if (!["SHA-1", "SHA-256", "SHA-384", "SHA-512"].includes(normalized)) {
    throw new Error(`unsupported hash algorithm ${algorithm}`);
  }

  const data = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest(normalized, data);
  return Array.from(new Uint8Array(digest))
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("");
}

function formatMathResult(value: unknown) {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("math result must be a finite number");
    }

    if (Number.isInteger(value)) {
      return String(value);
    }

    return String(Number(value.toFixed(6)));
  }

  return String(value);
}

function parseFiniteNumber(value: string) {
  const trimmed = value.trim();
  if (!trimmed || ["nan", "inf", "+inf", "-inf", "infinity", "+infinity", "-infinity"].includes(trimmed.toLowerCase())) {
    throw new Error(`invalid number ${value}`);
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`invalid number ${value}`);
  }
  return parsed;
}

function parseDecimalPlaces(value: string) {
  const places = Number.parseInt(value.trim(), 10);
  if (!/^-?[0-9]+$/.test(value.trim()) || places < 0) {
    throw new Error(`decimal places ${value} is invalid`);
  }
  return places;
}

function formatTransformNumber(value: number) {
  return formatMathResult(value);
}

function evaluateMathExpression(expression: string, currentInput: string, references: Record<string, string>) {
  let resolved = expression;
  const referencePattern = /\$([A-Za-z_][A-Za-z0-9_]*)/g;

  resolved = resolved.replace(referencePattern, (_match, key: string) => {
    if (key === "_self") {
      return currentInput;
    }

    if (key === "_PI") {
      return String(Math.PI);
    }

    if (key in references) {
      return references[key] ?? "";
    }

    throw new Error(`reference $${key} not found`);
  });

  if (!/^[0-9+\-*/%^().,\sA-Za-z_]+$/.test(resolved)) {
    throw new Error("math expression contains unsupported characters");
  }

  const round = (value: number) => Math.round(value);
  const floor = (value: number) => Math.floor(value);
  const ceil = (value: number) => Math.ceil(value);
  const abs = (value: number) => Math.abs(value);
  const float = (value: string | number) => Number.parseFloat(String(value));
  const int = (value: string | number) => Number.parseInt(String(value), 10);

  const evaluator = new Function("round", "floor", "ceil", "abs", "float", "int", `return (${resolved});`);
  return evaluator(round, floor, ceil, abs, float, int) as unknown;
}

const REGION_PHONE_PREFIX: Record<string, string> = {
  TH: "66",
  SG: "65",
  MY: "60",
  ID: "62",
  VN: "84",
  PH: "63",
  KH: "855",
  LA: "856",
  MM: "95",
  IN: "91",
  JP: "81",
  KR: "82",
  CN: "86",
  HK: "852",
  TW: "886",
  US: "1",
  CA: "1",
  AU: "61",
  NZ: "64",
  GB: "44",
};

function addRegionPhoneNumber(input: string, region: string) {
  const normalizedRegion = region.trim().toUpperCase();
  const prefix = REGION_PHONE_PREFIX[normalizedRegion];

  if (!prefix) {
    throw new Error(`unsupported phone region ${region}`);
  }

  if (!input.startsWith("0")) {
    return input;
  }

  return `${prefix}${input.slice(1)}`;
}

function buildPreviewMessage(kind: string, details: Record<string, string>) {
  const detailText = Object.entries(details)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${value}`)
    .join(", ");

  return detailText ? `[preview:${kind}] ${detailText}` : `[preview:${kind}]`;
}

function decodeValue(input: string, method: string) {
  const normalized = method.trim().toLowerCase();

  if (normalized === "url") {
    return decodeURIComponent(input);
  }

  if (normalized === "base64" || normalized === "base64url") {
    const base64 = normalized === "base64url"
      ? input.replace(/-/g, "+").replace(/_/g, "/")
      : input;
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(base64), (char: string) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
  }

  if (normalized === "hex") {
    const pairs = input.match(/.{1,2}/g) ?? [];
    return pairs.map((item) => String.fromCharCode(Number.parseInt(item, 16))).join("");
  }

  throw new Error(`unsupported decode method ${method}`);
}

async function executeSupportedFunction(
  statement: TransformFunctionStatement,
  currentInput: string,
  references: Record<string, string>
) {
  const params = statement.param.map((param) => resolveParamValue(param, currentInput, references));

  switch (statement.functionName) {
    case "trim_space":
      return { value: currentInput.trim(), renamedField: "", forceTerminate: false };
    case "lowercase":
      return { value: currentInput.toLowerCase(), renamedField: "", forceTerminate: false };
    case "uppercase":
      return { value: currentInput.toUpperCase(), renamedField: "", forceTerminate: false };
    case "collapse_space":
      return { value: currentInput.trim().replace(/\s+/g, " "), renamedField: "", forceTerminate: false };
    case "keep_digits":
      return { value: currentInput.replace(/\D+/g, ""), renamedField: "", forceTerminate: false };
    case "remove_prefix": {
      const prefix = params[0] ?? "";
      return { value: currentInput.startsWith(prefix) ? currentInput.slice(prefix.length) : currentInput, renamedField: "", forceTerminate: false };
    }
    case "remove_suffix": {
      const suffix = params[0] ?? "";
      return { value: suffix && currentInput.endsWith(suffix) ? currentInput.slice(0, -suffix.length) : currentInput, renamedField: "", forceTerminate: false };
    }
    case "default_if_empty": {
      const trimmed = currentInput.trim();
      return { value: trimmed ? trimmed : (params[0] ?? ""), renamedField: "", forceTerminate: false };
    }
    case "replace_if_not_number": {
      try {
        const trimmed = currentInput.trim();
        if (!trimmed) {
          return { value: params[0] ?? "", renamedField: "", forceTerminate: false };
        }
        parseFiniteNumber(trimmed);
        return { value: trimmed, renamedField: "", forceTerminate: false };
      } catch {
        return { value: params[0] ?? "", renamedField: "", forceTerminate: false };
      }
    }
    case "map_value": {
      if (params.length === 0 || params.length % 2 !== 0) {
        throw new Error("map_value requires from/to parameter pairs");
      }
      for (let index = 0; index < params.length; index += 2) {
        if (currentInput === (params[index] ?? "")) {
          return { value: params[index + 1] ?? "", renamedField: "", forceTerminate: false };
        }
      }
      throw new Error(`map_value did not match ${currentInput}`);
    }
    case "add":
      return { value: formatTransformNumber(parseFiniteNumber(currentInput) + parseFiniteNumber(params[0] ?? "")), renamedField: "", forceTerminate: false };
    case "subtract":
      return { value: formatTransformNumber(parseFiniteNumber(currentInput) - parseFiniteNumber(params[0] ?? "")), renamedField: "", forceTerminate: false };
    case "multiply":
      return { value: formatTransformNumber(parseFiniteNumber(currentInput) * parseFiniteNumber(params[0] ?? "")), renamedField: "", forceTerminate: false };
    case "divide": {
      const divisor = parseFiniteNumber(params[0] ?? "");
      if (divisor === 0) {
        throw new Error("divide by zero");
      }
      return { value: formatTransformNumber(parseFiniteNumber(currentInput) / divisor), renamedField: "", forceTerminate: false };
    }
    case "round": {
      const places = parseDecimalPlaces(params[0] ?? "0");
      const factor = 10 ** places;
      return { value: formatTransformNumber(Math.round(parseFiniteNumber(currentInput) * factor) / factor), renamedField: "", forceTerminate: false };
    }
    case "ceil":
      return { value: formatTransformNumber(Math.ceil(parseFiniteNumber(currentInput))), renamedField: "", forceTerminate: false };
    case "floor":
      return { value: formatTransformNumber(Math.floor(parseFiniteNumber(currentInput))), renamedField: "", forceTerminate: false };
    case "format_decimal": {
      const places = parseDecimalPlaces(params[0] ?? "0");
      return { value: parseFiniteNumber(currentInput).toFixed(places), renamedField: "", forceTerminate: false };
    }
    case "clamp": {
      const min = parseFiniteNumber(params[0] ?? "");
      const max = parseFiniteNumber(params[1] ?? "");
      if (min > max) {
        throw new Error("clamp min must be less than or equal to max");
      }
      return { value: formatTransformNumber(Math.min(Math.max(parseFiniteNumber(currentInput), min), max)), renamedField: "", forceTerminate: false };
    }
    case "sum_fields": {
      const sum = params.reduce((total, value) => total + parseFiniteNumber(value), 0);
      return { value: formatTransformNumber(sum), renamedField: "", forceTerminate: false };
    }
    case "prefix_with":
      return { value: `${params[0] ?? ""}${currentInput}`, renamedField: "", forceTerminate: false };
    case "subfix_with":
      return { value: `${currentInput}${params[0] ?? ""}`, renamedField: "", forceTerminate: false };
    case "concat":
      return {
        value: params[1] === "true" ? `${currentInput} ${params[0] ?? ""}` : `${currentInput}${params[0] ?? ""}`,
        renamedField: "",
        forceTerminate: false,
      };
    case "overwrite_value":
      return { value: params[0] ?? "", renamedField: "", forceTerminate: false };
    case "replace_string":
      return { value: currentInput.split(params[0] ?? "").join(params[1] ?? ""), renamedField: "", forceTerminate: false };
    case "split_string": {
      const index = Number.parseInt(params[1] ?? "0", 10);
      const parts = currentInput.split(params[0] ?? "");
      if (Number.isNaN(index) || index < 0 || index >= parts.length) {
        throw new Error(`split index ${params[1] ?? ""} out of bounds`);
      }
      return { value: parts[index] ?? "", renamedField: "", forceTerminate: false };
    }
    case "explode_by_pipe": {
      const index = Number.parseInt(params[0] ?? "0", 10);
      const parts = currentInput.split("|");
      if (Number.isNaN(index) || index < 0 || index >= parts.length) {
        throw new Error(`pipe index ${params[0] ?? ""} out of bounds`);
      }
      return { value: parts[index] ?? "", renamedField: "", forceTerminate: false };
    }
    case "repeat_string": {
      const times = Number.parseInt(params[0] ?? "0", 10);
      if (Number.isNaN(times) || times < 0) {
        throw new Error(`repeat count ${params[0] ?? ""} is invalid`);
      }
      return { value: currentInput.repeat(times), renamedField: "", forceTerminate: false };
    }
    case "read_from_json": {
      const parsed = JSON.parse(currentInput) as Record<string, unknown>;
      const path = (params[0] ?? "").split(".").filter(Boolean);
      let value: unknown = parsed;

      for (const segment of path) {
        if (value && typeof value === "object" && segment in value) {
          value = (value as Record<string, unknown>)[segment];
        } else {
          value = "";
          break;
        }
      }

      return { value: value == null ? "" : String(value), renamedField: "", forceTerminate: false };
    }
    case "read_from_array": {
      const parsed = JSON.parse(currentInput) as Array<Record<string, unknown>>;
      const key = params[0] ?? "";
      const value = parsed
        .map((item) => item?.[key])
        .filter((item) => item !== undefined && item !== null)
        .map((item) => String(item))
        .join(",");
      return { value, renamedField: "", forceTerminate: false };
    }
    case "build_column":
      return { value: params[1] ?? "", renamedField: params[0] ?? "", forceTerminate: false };
    case "exit_if_equal": {
      const shouldStop = currentInput === (params[0] ?? "");
      return {
        value: shouldStop ? (params[1] ?? "") : currentInput,
        renamedField: "",
        forceTerminate: shouldStop,
      };
    }
    case "autonumber":
      return { value: buildPreviewMessage("autonumber", { prefix: params[0] ?? "", value: `${params[0] ?? ""}1001` }), renamedField: "", forceTerminate: false };
    case "base64_to_s3":
      return { value: buildPreviewMessage("base64_to_s3", { path: params[0] ?? "", prefix: params[1] ?? "", result: "uploaded-to-s3" }), renamedField: "", forceTerminate: false };
    case "encrypt_to_s3":
      return { value: buildPreviewMessage("encrypt_to_s3", { path: params[0] ?? "", prefix: params[1] ?? "", result: "encrypted-and-uploaded-to-s3" }), renamedField: "", forceTerminate: false };
    case "s3_to_decrypt":
      return { value: buildPreviewMessage("s3_to_decrypt", { source: currentInput, result: "decrypted-from-s3" }), renamedField: "", forceTerminate: false };
    case "s3_to_base64":
      return { value: buildPreviewMessage("s3_to_base64", { source: currentInput, storage: params[0] ?? "private", result: "base64-from-s3" }), renamedField: "", forceTerminate: false };
    case "generate_jwt":
      return { value: buildPreviewMessage("generate_jwt", { issuer: params[0] ?? "", secret: params[1] ? "provided" : "missing", result: "jwt-generated-on-server" }), renamedField: "", forceTerminate: false };
    case "hash":
      return { value: await shaHash(params[0] ?? "SHA-256", currentInput), renamedField: "", forceTerminate: false };
    case "datetime":
    case "to_system_datetime": {
      const pattern = params[0] ?? "yyyy-MM-dd HH:mm:ss";
      const parsed = parseDatePartsWithPattern(currentInput, pattern);
      return { value: formatDatePartsWithPattern(parsed, "yyyy-MM-dd HH:mm:ss"), renamedField: "", forceTerminate: false };
    }
    case "datetime_to_unix_timestamp": {
      const date = parseBestEffortDateParts(currentInput);
      const sourceOffset = Number.parseInt(params[0] ?? "0", 10);
      if (Number.isNaN(sourceOffset)) {
        throw new Error(`invalid timezone offset ${params[0]}`);
      }
      return { value: String(datePartsToUnixSeconds(shiftDatePartsByUtcOffset(date, -sourceOffset))), renamedField: "", forceTerminate: false };
    }
    case "unix_timestamp_to_datetime": {
      const timestamp = Number.parseInt(currentInput, 10);
      if (Number.isNaN(timestamp)) {
        throw new Error(`invalid unix timestamp ${currentInput}`);
      }
      return { value: formatDatePartsWithPattern(unixSecondsToDateParts(timestamp), "yyyy-MM-dd HH:mm:ss"), renamedField: "", forceTerminate: false };
    }
    case "format_date_string":
    case "from_system_datetime": {
      const pattern = params[0] ?? "yyyy-MM-dd HH:mm:ss";
      const date = parseBestEffortDateParts(currentInput);
      return { value: formatDatePartsWithPattern(date, pattern), renamedField: "", forceTerminate: false };
    }
    case "convert_timezone": {
      const sourceOffset = Number.parseInt(params[0] ?? "0", 10);
      const destinationOffset = Number.parseInt(params[1] ?? "0", 10);
      if (Number.isNaN(sourceOffset) || Number.isNaN(destinationOffset)) {
        throw new Error("timezone offsets must be integers");
      }
      const parsed = parseBestEffortDateParts(currentInput);
      const utcDate = shiftDatePartsByUtcOffset(parsed, -sourceOffset);
      const converted = shiftDatePartsByUtcOffset(utcDate, destinationOffset);
      return { value: formatDatePartsWithPattern(converted, "yyyy-MM-dd HH:mm:ss"), renamedField: "", forceTerminate: false };
    }
    case "hex_utf16": {
      const bytes = new Uint8Array(currentInput.length * 2);
      for (let index = 0; index < currentInput.length; index += 1) {
        const code = currentInput.charCodeAt(index);
        bytes[index * 2] = code & 0xff;
        bytes[index * 2 + 1] = code >> 8;
      }
      return { value: Array.from(bytes).map((item) => item.toString(16).padStart(2, "0")).join(""), renamedField: "", forceTerminate: false };
    }
    case "math_expressions":
      return { value: formatMathResult(evaluateMathExpression(params[0] ?? "", currentInput, references)), renamedField: "", forceTerminate: false };
    case "add_region_phone_number":
      return { value: addRegionPhoneNumber(currentInput, params[0] ?? ""), renamedField: "", forceTerminate: false };
    case "convert_redirect_id_to_contact_id":
      return { value: buildPreviewMessage("convert_redirect_id_to_contact_id", { redirectId: currentInput, result: "contact-id-from-redirect" }), renamedField: "", forceTerminate: false };
    case "decode":
      return { value: decodeValue(currentInput, params[0] ?? "base64"), renamedField: "", forceTerminate: false };
    default:
      throw new Error(`offline preview does not support function ${statement.functionName}`);
  }
}

export async function evaluateTransformScriptOffline(
  script: string,
  input: TransformScriptEvaluationInput
): Promise<TransformScriptEvaluationResult> {
  const statements = parseScript(script);
  return evaluateTransformStatementsOffline(statements, input);
}

/**
 * Evaluate an already parsed transform-script AST. This keeps preview execution
 * independent from the parser implementation and lets parity tests verify
 * downstream behavior without changing the production parser.
 */
export async function evaluateTransformStatementsOffline(
  statements: TransformFunctionStatement[],
  input: TransformScriptEvaluationInput
): Promise<TransformScriptEvaluationResult> {
  const bindingContext: TransformScriptBindingContext = {
    selfValue: input.input,
    hasSelf: true,
    refs: input.references,
  };
  const resolvedReferences = Object.fromEntries(
    Object.entries(input.references).map(([key, value]) => [
      key,
      resolveTransformScriptBindingsInTemplate(value, {
        ...bindingContext,
        selfValue: value,
        hasSelf: true,
      }),
    ])
  );
  let currentValue = resolveTransformScriptBindingsInTemplate(input.input, {
    ...bindingContext,
    refs: resolvedReferences,
  });
  let renamedField = "";
  const unsupportedFunctions: string[] = [];

  for (const statement of statements) {
    if (!OFFLINE_SUPPORTED_FUNCTIONS.has(statement.functionName)) {
      unsupportedFunctions.push(statement.functionName);
      continue;
    }

    try {
      const result = await executeSupportedFunction(statement, currentValue, resolvedReferences);
      currentValue = result.value;
      if (result.renamedField) {
        renamedField = resolveTransformScriptBinding(result.renamedField, {
          selfValue: currentValue,
          hasSelf: true,
          refs: resolvedReferences,
        });
      }
      if (result.forceTerminate) {
        break;
      }
    } catch (error) {
      currentValue = resolveDefaultValue(statement.defaultValue, currentValue, resolvedReferences);
      if (error instanceof Error && error.message.includes("offline preview does not support")) {
        unsupportedFunctions.push(statement.functionName);
      }
    }
  }

  return {
    output: currentValue,
    renamedField,
    unsupportedFunctions: Array.from(new Set(unsupportedFunctions)),
  };
}
